import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Alert } from 'components/Toast';
import { toast } from 'react-toastify';
import { services } from 'service';
import {
  AdminDashStat,
  CreateStaffDto,
  CustomerListType,
  StaffListType,
  StaffType
} from 'service/dto/staff.dto';
import { ApiPaginationOptions } from 'types/general.type';

export const useCreateStaff = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: CreateStaffDto): Promise<any> => {
      return services.manageStaffService.createStaff(payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['create-staff']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

type QueryParams = {
  whereOptions: any;
  paginationOptions: ApiPaginationOptions;
  enabled: boolean;
};

export const useGetStaffList = ({
  whereOptions,
  paginationOptions,
  enabled
}: QueryParams) => {
  const staffList = useQuery<StaffListType, AxiosError<Error>>(
    ['staff_list', paginationOptions, whereOptions],
    () =>
      services.manageStaffService.getStaffs(whereOptions, paginationOptions),

    {
      keepPreviousData: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled
    }
  );
  return staffList;
};

export const useGetStaffDetails = ({ staffId }: { staffId: number }) => {
  return useQuery<StaffType, AxiosError<Error>>(
    ['get_staff', staffId],
    () => services.manageStaffService.getStaff(staffId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!staffId
    }
  );
};

export const useDeactiveActivateStaff = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ staffId }: { staffId: number }): Promise<any> => {
      return services.manageStaffService.toggleStaffActive(staffId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['toggle-staff']);
        queryClient.invalidateQueries(['staff_customer_list']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetStaffCustomerList = ({
  whereOptions,
  paginationOptions,
  enabled
}: QueryParams) => {
  const staffCutomerList = useQuery<CustomerListType, AxiosError<Error>>(
    ['staff_customer_list', paginationOptions, whereOptions],
    () =>
      services.manageStaffService.getStaffCustomers(
        whereOptions,
        paginationOptions
      ),

    {
      keepPreviousData: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled
    }
  );
  return staffCutomerList;
};

export const useGetAdminDashboardStat = () => {
  return useQuery<AdminDashStat, AxiosError<Error>>(
    ['get_admin_stat'],
    () => services.manageStaffService.getAdminDashboardStat(),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false
    }
  );
};

export const useGetStaffActivities = () =>
  useQuery(
    ['staff_activities'],
    () => services.manageStaffService.getStaffActivities(),
    {
      refetchOnWindowFocus: false
    }
  );

export const useGetCustomerProspect = () =>
  useQuery(
    ['customer_prospect'],
    () => services.manageStaffService.getCustomerProspect(),
    {
      refetchOnWindowFocus: false
    }
  );
